import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  alpha,
} from "@mui/material";
import FollowTheSignsOutlinedIcon from "@mui/icons-material/FollowTheSignsOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import axios from "axios";
import { WEBSERVICE } from "../../CoreDataService";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";

const COPYTRADINGSERVICE = WEBSERVICE + "/copyTrading";

const WhomIFollow = () => {
  const navigate = useNavigate();
  const { btncolor } = useSelector((state) => state);
  const [myFollowers, setMyFollowers] = useState([]);

  useEffect(() => {
    getMyFollowers();
  }, []);

  const getMyFollowers = async () => {
    try {
      const response = await axios.get(
        `${COPYTRADINGSERVICE}/apiKey/getWhomeIFollowDetails/${localStorage.getItem("userId")}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setMyFollowers(response.data);
    } catch (error) {
      console.error("Error fetching followers", error);
    }
  };

  const stats = [
    {
      label: "Traders I Follow",
      value: myFollowers.apiCount ?? "—",
      icon: PersonOutlinedIcon,
      desc: "Traders you are copy-following",
    },
    {
      label: "Bots I Follow",
      value: myFollowers.botCount ?? "—",
      icon: SmartToyOutlinedIcon,
      desc: "Trading bots you are subscribed to",
    },
  ];

  return (
    <Box sx={pageWrapper}>
      {/* Hero Banner */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <FollowTheSignsOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={heroTitle}>Whom I Follow</Typography>
            <Typography sx={heroSubtitle}>
              Traders and bots you are currently copy-following
            </Typography>
          </Box>
          <ContainedButton
            variant="contained"
            onClick={() => navigate("/copy-trade")}
            sx={backBtn}
          >
            Back
          </ContainedButton>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={statsGrid}>
        {stats.map((stat, i) => (
          <Box key={i} sx={statCard}>
            <Box
              sx={{
                ...statIconWrap,
                backgroundColor: alpha(btncolor || "#1976d2", 0.1),
                color: btncolor,
              }}
            >
              <stat.icon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={statLabel}>{stat.label}</Typography>
              <Typography sx={statDesc}>{stat.desc}</Typography>
            </Box>
            <Box sx={statValueWrap}>
              <Typography sx={statValue}>{stat.value}</Typography>
              <Typography sx={statValueLabel}>following</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
  flexWrap: "wrap",
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 540,
};

const backBtn = {
  height: 40,
  px: 2.5,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.85rem",
  flexShrink: 0,
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.3)",
  color: "#fff",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 2,
};

const statCard = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  p: 2.5,
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const statIconWrap = {
  width: 48,
  height: 48,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const statLabel = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const statDesc = {
  fontSize: "0.75rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.3,
};

const statValueWrap = {
  textAlign: "right",
  flexShrink: 0,
};

const statValue = {
  fontSize: "2rem",
  fontWeight: 800,
  color: "var(--text-color)",
  lineHeight: 1,
  letterSpacing: "-0.02em",
};

const statValueLabel = {
  fontSize: "0.65rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.5,
  mt: 0.25,
};

export default WhomIFollow;
